import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';


const FinancialTools = ({ property, similarProperties }) => {
  const [activeCalculator, setActiveCalculator] = useState('emi');
  const [emiInputs, setEmiInputs] = useState({
    loanAmount: property.price * 0.8,
    interestRate: 8.5,
    tenure: 20
  });
  const [rentalInputs, setRentalInputs] = useState({
    monthlyRent: property.type === 'rent' ? property.price : 25000,
    maintenanceCost: 2000,
    propertyTax: 5000
  });

  const calculators = [
    { id: 'emi', label: 'EMI Calculator', icon: 'Calculator' },
    { id: 'rental', label: 'Rental Yield', icon: 'TrendingUp' },
    { id: 'comparison', label: 'Price Comparison', icon: 'BarChart3' }
  ];

  const calculateEMI = () => {
    const { loanAmount, interestRate, tenure } = emiInputs;
    const monthlyRate = interestRate / 12 / 100;
    const months = tenure * 12;
    
    const emi = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                (Math.pow(1 + monthlyRate, months) - 1);
    
    return {
      emi: Math.round(emi),
      totalAmount: Math.round(emi * months),
      totalInterest: Math.round((emi * months) - loanAmount)
    };
  };

  const calculateRentalYield = () => {
    const { monthlyRent, maintenanceCost, propertyTax } = rentalInputs;
    const annualRent = monthlyRent * 12;
    const annualExpenses = (maintenanceCost * 12) + propertyTax;
    const netRent = annualRent - annualExpenses;
    const yieldPercentage = (netRent / property.price) * 100;
    
    return {
      annualRent,
      annualExpenses,
      netRent,
      yieldPercentage: yieldPercentage.toFixed(2)
    };
  };

  const emiResult = calculateEMI();
  const rentalResult = calculateRentalYield();

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)} L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  return (
    <div className="bg-surface rounded-lg shadow-property-card">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-text-primary mb-4">
          Financial Tools
        </h2>
        
        {/* Calculator Tabs */}
        <div className="flex flex-wrap gap-2">
          {calculators.map((calc) => (
            <button
              key={calc.id}
              onClick={() => setActiveCalculator(calc.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                activeCalculator === calc.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={calc.icon} size={16} />
              <span>{calc.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* EMI Calculator */}
        {activeCalculator === 'emi' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Loan Amount"
                type="number"
                value={emiInputs.loanAmount}
                onChange={(e) => setEmiInputs({...emiInputs, loanAmount: Number(e.target.value)})}
                placeholder="Enter loan amount"
              />
              <Input
                label="Interest Rate (%)"
                type="number"
                step="0.1"
                value={emiInputs.interestRate}
                onChange={(e) => setEmiInputs({...emiInputs, interestRate: Number(e.target.value)})}
                placeholder="Enter interest rate"
              />
              <Input
                label="Tenure (Years)"
                type="number"
                value={emiInputs.tenure}
                onChange={(e) => setEmiInputs({...emiInputs, tenure: Number(e.target.value)})}
                placeholder="Enter tenure"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="text-2xl font-bold text-primary mb-1">
                  {formatCurrency(emiResult.emi)}
                </div>
                <div className="text-sm text-text-secondary">Monthly EMI</div>
              </div>
              
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-text-primary mb-1">
                  {formatCurrency(emiResult.totalAmount)}
                </div>
                <div className="text-sm text-text-secondary">Total Amount</div>
              </div>
              
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-text-primary mb-1">
                  {formatCurrency(emiResult.totalInterest)}
                </div>
                <div className="text-sm text-text-secondary">Total Interest</div>
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">Loan Breakdown</span>
              </div>
              <div className="text-sm text-text-secondary space-y-1">
                <div>Down Payment (20%): {formatCurrency(property.price * 0.2)}</div>
                <div>Processing Fee (1%): {formatCurrency(emiInputs.loanAmount * 0.01)}</div>
                <div>Registration & Stamp Duty: {formatCurrency(property.price * 0.07)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Rental Yield Calculator */}
        {activeCalculator === 'rental' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Monthly Rent"
                type="number"
                value={rentalInputs.monthlyRent}
                onChange={(e) => setRentalInputs({...rentalInputs, monthlyRent: Number(e.target.value)})}
                placeholder="Enter monthly rent"
              />
              <Input
                label="Monthly Maintenance"
                type="number"
                value={rentalInputs.maintenanceCost}
                onChange={(e) => setRentalInputs({...rentalInputs, maintenanceCost: Number(e.target.value)})}
                placeholder="Enter maintenance cost"
              />
              <Input
                label="Annual Property Tax"
                type="number"
                value={rentalInputs.propertyTax}
                onChange={(e) => setRentalInputs({...rentalInputs, propertyTax: Number(e.target.value)})}
                placeholder="Enter property tax"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-secondary/5 rounded-lg border border-secondary/20">
                <div className="text-3xl font-bold text-secondary mb-1">
                  {rentalResult.yieldPercentage}%
                </div>
                <div className="text-sm text-text-secondary">Annual Rental Yield</div>
              </div>
              
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-2xl font-bold text-text-primary mb-1">
                  {formatCurrency(rentalResult.netRent)}
                </div>
                <div className="text-sm text-text-secondary">Net Annual Income</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-border/50">
                <span className="text-text-secondary">Annual Rental Income</span>
                <span className="font-medium text-text-primary">{formatCurrency(rentalResult.annualRent)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/50">
                <span className="text-text-secondary">Annual Expenses</span>
                <span className="font-medium text-error">-{formatCurrency(rentalResult.annualExpenses)}</span>
              </div>
              <div className="flex justify-between items-center py-2 font-semibold">
                <span className="text-text-primary">Net Annual Income</span>
                <span className="text-secondary">{formatCurrency(rentalResult.netRent)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Price Comparison */}
        {activeCalculator === 'comparison' && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Price Comparison with Similar Properties
              </h3>
              <p className="text-text-secondary text-sm">
                Compare this property with similar listings in the area
              </p>
            </div>

            <div className="space-y-4">
              {similarProperties.map((similar, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-text-primary">{similar.title}</div>
                    <div className="text-sm text-text-secondary">{similar.location}</div>
                    <div className="text-xs text-text-secondary mt-1">
                      {similar.configuration} • {similar.area} sq ft
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-bold text-text-primary">
                      {formatCurrency(similar.price)}
                    </div>
                    <div className="text-sm text-text-secondary">
                      ₹{similar.pricePerSqFt}/sq ft
                    </div>
                    <div className={`text-xs font-medium ${
                      similar.price < property.price ? 'text-success' : 
                      similar.price > property.price ? 'text-error' : 'text-text-secondary'
                    }`}>
                      {similar.price < property.price ? 'Lower' : 
                       similar.price > property.price ? 'Higher' : 'Same'}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">Market Analysis</span>
              </div>
              <div className="text-sm text-text-secondary space-y-1">
                <div>Average price in area: ₹{((similarProperties.reduce((sum, p) => sum + p.price, 0) / similarProperties.length) / 100000).toFixed(1)} L</div>
                <div>This property is {property.price > (similarProperties.reduce((sum, p) => sum + p.price, 0) / similarProperties.length) ? 'above' : 'below'} market average</div>
                <div>Price trend: Stable with 3% growth in last 6 months</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialTools;